
import React, { useMemo, useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

export default function PricingComparison() {
  const [totalTickets, setTotalTickets] = useState(12000);
  const [avgTicket, setAvgTicket] = useState(50);
  const [onlineReservations, setOnlineReservations] = useState(3650);
  const [numEmployees, setNumEmployees] = useState(20);

  const [selectedPosProvider, setSelectedPosProvider] = useState("toast");
  const [selectedReservationProvider, setSelectedReservationProvider] = useState("opentable"); // null = none (no third-party platform)

  const [gohostTxnFee, setGohostTxnFee] = useState(0.50); // dollars — fetched from gohost_config/platform

  useEffect(() => {
    getDoc(doc(db, "gohost_config", "platform")).then((snap) => {
      if (snap.exists()) {
        const cents = snap.data().defaultTransactionFeeCents;
        if (typeof cents === "number" && cents > 0) setGohostTxnFee(cents / 100);
      }
    }).catch(() => {/* keep default */});
  }, []);

  const [selectedServices, setSelectedServices] = useState([
    "pos",
    "processing",
    "website",
    "ordering",
    "loyalty",
    "sms",
    "giftcards",
    "reservations",
    "payroll",
    "advancedMenu",
    "basicReporting",
    "waitlistReservations",
    "employeeScheduling",
  ]);

  const SERVICES = [
    { key: "pos",         label: "Table Management & Guest Notes" },
    { key: "processing",  label: "Card Processing" },
    { key: "website",     label: "Website" },
    { key: "ordering",    label: "Online Ordering / Preorders" },
    { key: "loyalty",     label: "Loyalty" },
    { key: "sms",         label: "SMS Messaging" },
    { key: "giftcards",   label: "Gift Cards" },
    { key: "reservations",        label: "Online Reservations" },
    { key: "payroll",             label: "Payroll" },
    { key: "advancedMenu",        label: "Advanced Menu Management" },
    { key: "basicReporting",      label: "Basic Reporting & Inventory" },
    { key: "waitlistReservations", label: "Unlimited Waitlist & Reservations" },
    { key: "employeeScheduling",   label: "Employee Scheduling" },
  ];

  const PRICING = {
    gohost: {
      processing:   { percent: 0.027, stripeFixed: 0.05, gohostFixed: gohostTxnFee, label: `2.7% & $0.05 Stripe + $${gohostTxnFee.toFixed(2)} GoHost` },
      pos:          { yearly: 0,    label: "Included" },
      website:      { yearly: 0,    label: "Included" },
      ordering:     { yearly: 0,    label: "Included" },
      loyalty:      { yearly: 0,    label: "Included" },
      sms:          { perCustomer: 0.02, yearly: 0, label: "Included", labelSuffix: "+ $0.02/SMS" },
      giftcards:    { yearly: 0,    label: "Included" },
      reservations:         { yearly: 0, perCover: 0, label: "Included" },
      payroll:              { yearly: 0, label: "Included" },
      advancedMenu:         { yearly: 0, label: "Included" },
      basicReporting:       { yearly: 0, label: "Included" },
      waitlistReservations: { yearly: 0, label: "Included" },
      employeeScheduling:   { yearly: 0, label: "Included" },
    },
    clover: {
      processing: { percent: 0.0249, fixed: 0.15, label: "2.49% & $0.15" },
      pos:         { yearly: 1440, label: "~$120/mo" },
      website:     { yearly: 900,  label: "$75/mo" },
      ordering:    { yearly: 720,  label: "$60/mo" },
      loyalty:     { yearly: 540,  label: "$45/mo" },
      sms:         { yearly: 600,  perSms: 0.02,  label: "~$50/mo + $0.02/SMS" },
      giftcards:            { yearly: 600,  label: "$50/mo" },
      payroll:              { baseYearly: 1320, perEmployeeYearly: 48, label: "$110/mo + $4/employee/mo" },
      advancedMenu:         { yearly: 600,  label: "$50/mo" },
      basicReporting:       { yearly: 0,    label: "Included" },
      waitlistReservations: { yearly: 2388, label: "$199/mo" },
      employeeScheduling:   { perEmployeeYearly: 120, label: "$10/employee/mo" },
    },
    square: {
      processing: { percent: 0.026, fixed: 0.15, label: "2.6% & $0.15" },
      pos:         { yearly: 720,  label: "$60/mo" },
      website:     { yearly: 0,    label: "Included" },
      ordering:    { yearly: 0,    label: "Included" },
      loyalty:     { yearly: 720,  label: "$60/mo" },
      sms:         { yearly: 960,  perSms: 0.015, label: "~$80/mo + $0.015/SMS" },
      giftcards:            { yearly: 240,  label: "~$20/mo / physical card costs vary" },
      payroll:              { baseYearly: 420, perEmployeeYearly: 72, label: "$35/mo + $6/employee/mo" },
      advancedMenu:         { yearly: 720,  label: "$60/mo" },
      basicReporting:       { yearly: 0,    label: "Included" },
      waitlistReservations: { yearly: 720,  label: "$60/mo" },
      employeeScheduling:   { perEmployeeYearly: 54, label: "$4.50/employee/mo" },
    },
    lightspeed: {
      processing: { percent: 0.026, fixed: 0.10, label: "~2.6% & $0.10" },
      pos:         { yearly: 1440, label: "$120/mo" },
      website:     { yearly: 900,  label: "$75/mo / external or add-on" },
      ordering:    { yearly: 720,  label: "$60/mo" },
      loyalty:     { yearly: 720,  label: "$60/mo" },
      sms:         { yearly: 720,  perSms: 0.02,  label: "~$60/mo + $0.02/SMS" },
      giftcards:            { yearly: 480,  label: "~$40/mo" },
      payroll:              { baseYearly: 480, perEmployeeYearly: 72, label: "$40/mo + $6/employee/mo" },
      advancedMenu:         { yearly: 840,  label: "$70/mo" },
      basicReporting:       { yearly: 0,    label: "Included" },
      waitlistReservations: { yearly: 3000, label: "$250/mo" },
      employeeScheduling:   { perEmployeeYearly: 0, label: "Included (higher-tier POS bundle)" },
    },
    toast: {
      processing: { percent: 0.0249, fixed: 0.15, label: "2.49% & $0.15" },
      pos:         { yearly: 828,   label: "$69/mo" },
      website:     { yearly: 900,   label: "$75/mo" },
      ordering:    { yearly: 900,   label: "$75/mo" },
      loyalty:     { yearly: 900,   label: "$75/mo (Marketing Suite)" },
      sms:         { yearly: 900,   perSms: 0, label: "$75/mo (Marketing Suite)" },
      giftcards:            { yearly: 300,  label: "~$25/mo" },
      payroll:              { baseYearly: 72, perEmployeeYearly: 72, label: "$6/mo + $6/employee/mo" },
      advancedMenu:         { yearly: 0,    label: "Included" },
      basicReporting:       { yearly: 0,    label: "Included" },
      waitlistReservations: { yearly: 1068, label: "$89/mo (Toast Tables)" },
      employeeScheduling:   { perEmployeeYearly: 48, label: "$4/employee/mo" },
    },
    spoton: {
      processing: { percent: 0.0289, fixed: 0.25, label: "~2.89% & $0.25" },
      pos:         { yearly: 1188,  label: "$99/mo" },
      website:     { yearly: 0,     label: "Included" },
      ordering:    { yearly: 0,     label: "Included" },
      loyalty:     { yearly: 300,   label: "~$25/mo" },
      sms:         { yearly: 1188,  perSms: 0, label: "~$99/mo (Marketing)" },
      giftcards:            { yearly: 300,  label: "~$25/mo" },
      payroll:              { baseYearly: 480, perEmployeeYearly: 72, label: "$40/mo + $6/employee/mo (integration)" },
      advancedMenu:         { yearly: 0,    label: "Included" },
      basicReporting:       { yearly: 0,    label: "Included" },
      waitlistReservations: { yearly: 1188, label: "$99/mo" },
      employeeScheduling:   { perEmployeeYearly: 48, label: "$4/employee/mo" },
    },
    opentable: {
      reservations: { yearly: 3588, perCover: 1,    label: "$299/mo + $1/cover" },
    },
    resy: {
      reservations: { yearly: 4788, perCover: 0.25, label: "$399/mo + $0.25/cover" },
    },
  };

  const formatMoney = (value) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

  const formatMoneyPrecise = (value) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

  const toggleService = (serviceKey) => {
    setSelectedServices((prev) =>
      prev.includes(serviceKey)
        ? prev.filter((item) => item !== serviceKey)
        : [...prev, serviceKey]
    );
  };

  const processedVolume = useMemo(() => totalTickets * avgTicket, [totalTickets, avgTicket]);

  const processingBreakdown = useMemo(() => {
    const provider = PRICING[selectedPosProvider].processing;
    const g = PRICING.gohost.processing;
    const gStripe = processedVolume * g.percent + totalTickets * g.stripeFixed;
    const gGoHost = totalTickets * g.gohostFixed;
    return {
      competitorTotal: processedVolume * provider.percent + totalTickets * provider.fixed,
      gohostTotal:     gStripe + gGoHost,
      gohostStripe:    gStripe,
      gohostFee:       gGoHost,
    };
  }, [processedVolume, totalTickets, selectedPosProvider, gohostTxnFee]);

  const totals = useMemo(() => {
    const pos = PRICING[selectedPosProvider];
    const res = selectedReservationProvider ? PRICING[selectedReservationProvider] : null;

    // --- Processing ---
    let gProcessing = 0, gProcessingStripe = 0, gProcessingGoHost = 0, cProcessing = 0;
    if (selectedServices.includes("processing")) {
      const g = PRICING.gohost.processing;
      gProcessingStripe = processedVolume * g.percent + totalTickets * g.stripeFixed;
      gProcessingGoHost = totalTickets * g.gohostFixed;
      gProcessing = gProcessingStripe + gProcessingGoHost;
      cProcessing = processedVolume * pos.processing.percent + totalTickets * pos.processing.fixed;
    }

    // --- Software & Services (all non-processing, non-reservation) ---
    let gSoftware = 0, cSoftware = 0;
    const gSoftwareItems = []; // per-service breakdown for GoHost transparency display
    const cSoftwareItems = []; // per-service breakdown for competitor transparency display

    const addSoftware = (key, label, gAmount, cAmount) => {
      gSoftware += gAmount;
      cSoftware += cAmount;
      gSoftwareItems.push({ label, amount: Math.round(gAmount) });
      cSoftwareItems.push({ label, amount: Math.round(cAmount) });
    };

    if (selectedServices.includes("pos"))
      addSoftware("pos", "Table Management & Guest Notes", PRICING.gohost.pos.yearly, pos.pos.yearly);
    if (selectedServices.includes("website"))
      addSoftware("website", "Website", PRICING.gohost.website.yearly, pos.website.yearly);
    if (selectedServices.includes("ordering"))
      addSoftware("ordering", "Online Ordering / Preorders", PRICING.gohost.ordering.yearly, pos.ordering.yearly);
    if (selectedServices.includes("loyalty"))
      addSoftware("loyalty", "Loyalty", PRICING.gohost.loyalty.yearly, pos.loyalty.yearly);
    if (selectedServices.includes("sms")) {
      // SMS uses onlineReservations — not all covers receive an SMS
      const gSms = onlineReservations * PRICING.gohost.sms.perCustomer + PRICING.gohost.sms.yearly;
      const cSms = pos.sms.yearly + onlineReservations * (pos.sms.perSms || 0);
      addSoftware("sms", "SMS Messaging", gSms, cSms);
    }
    if (selectedServices.includes("giftcards"))
      addSoftware("giftcards", "Gift Cards", PRICING.gohost.giftcards.yearly, pos.giftcards.yearly);
    if (selectedServices.includes("payroll"))
      addSoftware("payroll", "Payroll", PRICING.gohost.payroll.yearly, pos.payroll.baseYearly + numEmployees * pos.payroll.perEmployeeYearly);
    if (selectedServices.includes("advancedMenu"))
      addSoftware("advancedMenu", "Advanced Menu Management", PRICING.gohost.advancedMenu.yearly, pos.advancedMenu.yearly);
    if (selectedServices.includes("basicReporting"))
      addSoftware("basicReporting", "Basic Reporting & Inventory", PRICING.gohost.basicReporting.yearly, pos.basicReporting.yearly);
    if (selectedServices.includes("waitlistReservations"))
      addSoftware("waitlistReservations", "Unlimited Waitlist & Reservations", PRICING.gohost.waitlistReservations.yearly, pos.waitlistReservations.yearly);
    if (selectedServices.includes("employeeScheduling"))
      addSoftware("employeeScheduling", "Employee Scheduling", PRICING.gohost.employeeScheduling.yearly, numEmployees * pos.employeeScheduling.perEmployeeYearly);

    // --- Reservations (per-cover uses onlineReservations — platforms only see online bookings) ---
    // cReservations is 0 when no third-party platform is selected (user handles reservations in-house)
    let gReservations = 0, cReservations = 0;
    if (selectedServices.includes("reservations")) {
      gReservations = PRICING.gohost.reservations.yearly + onlineReservations * PRICING.gohost.reservations.perCover;
      if (res) cReservations = res.reservations.yearly + onlineReservations * res.reservations.perCover;
    }

    const gohostTotal     = gProcessing + gSoftware + gReservations;
    const competitorTotal = cProcessing + cSoftware + cReservations;

    const anySoftware = ["pos","website","ordering","loyalty","sms","giftcards"].some((k) => selectedServices.includes(k));

    return {
      gohost:     Math.round(gohostTotal),
      competitor: Math.round(competitorTotal),
      savings:    Math.round(competitorTotal - gohostTotal),
      subtotals: {
        gohost: {
          processing:       Math.round(gProcessing),
          processingStripe: Math.round(gProcessingStripe),
          processingGoHost: Math.round(gProcessingGoHost),
          software:         Math.round(gSoftware),
          softwareItems:    gSoftwareItems,
          reservations:     Math.round(gReservations),
        },
        competitor: { processing: Math.round(cProcessing), software: Math.round(cSoftware), softwareItems: cSoftwareItems, reservations: Math.round(cReservations) },
      },
      showProcessing:   selectedServices.includes("processing"),
      showSoftware:     anySoftware,
      showReservations: selectedServices.includes("reservations"),
    };
  }, [processedVolume, totalTickets, onlineReservations, numEmployees, selectedPosProvider, selectedReservationProvider, selectedServices, gohostTxnFee]);

  const labelClass = (label) => label.startsWith("Included") ? "text-green-700" : "text-red-700";

  const cell = "border-b border-neutral-200 px-2 py-1";

  const BRAND = {
    gohost:    { th: "bg-amber-300 text-amber-900",     col: "bg-amber-50",   text: "text-amber-800" },
    clover:    { th: "bg-orange-300 text-orange-950",   col: "bg-orange-50",  text: "text-orange-800", sel: "border-orange-800 bg-orange-800 text-white",   unsel: "border-orange-300 bg-white text-orange-800" },
    square:    { th: "bg-sky-200 text-sky-900",         col: "bg-sky-50",     text: "text-sky-700",    sel: "border-sky-600 bg-sky-600 text-white",          unsel: "border-sky-200 bg-white text-sky-700" },
    lightspeed:{ th: "bg-emerald-200 text-emerald-900", col: "bg-emerald-50", text: "text-emerald-700",sel: "border-emerald-700 bg-emerald-700 text-white",  unsel: "border-emerald-200 bg-white text-emerald-700" },
    toast:     { th: "bg-red-400 text-white",           col: "bg-red-50",     text: "text-red-700",    sel: "border-red-600 bg-red-600 text-white",          unsel: "border-red-300 bg-white text-red-700" },
    spoton:    { th: "bg-blue-500 text-white",          col: "bg-blue-50",    text: "text-blue-700",   sel: "border-blue-600 bg-blue-600 text-white",        unsel: "border-blue-300 bg-white text-blue-700" },
    opentable: { th: "bg-red-200 text-red-900",         col: "bg-red-50",     text: "text-red-700",    sel: "border-red-700 bg-red-700 text-white",          unsel: "border-red-200 bg-white text-red-700" },
    resy:      { th: "bg-orange-200 text-orange-800",   col: "bg-orange-50",  text: "text-orange-600", sel: "border-orange-600 bg-orange-600 text-white",   unsel: "border-orange-200 bg-white text-orange-600" },
  };

  const PROVIDER_DISPLAY_NAME = {
    clover: "Clover", square: "Square", lightspeed: "Lightspeed", toast: "Toast", spoton: "SpotOn",
  };

  const reservationProviderLabel = selectedReservationProvider === "opentable" ? "OpenTable" : selectedReservationProvider === "resy" ? "Resy" : null;
  const selectedStackLabel = `${PROVIDER_DISPLAY_NAME[selectedPosProvider] || (selectedPosProvider.charAt(0).toUpperCase() + selectedPosProvider.slice(1))}${reservationProviderLabel ? ` + ${reservationProviderLabel}` : ""}`;

  // Subtotal row helper
  const SubtotalRow = ({ label, gohost, competitor, gColor, cColor }) => (
    <div className="flex justify-between text-sm py-0.5">
      <span className="text-neutral-500 w-1/3">{label}</span>
      <span className={`font-medium w-1/3 text-right ${gColor}`}>{formatMoney(gohost)}</span>
      <span className={`font-medium w-1/3 text-right ${cColor}`}>{formatMoney(competitor)}</span>
    </div>
  );

  return (
    <div className="w-full px-4 py-12 md:px-8">
      <div className="mx-auto max-w-7xl space-y-4">

        {/* Title */}
        <div className="space-y-1 text-center">
          <h2 className="h2 font-semibold tracking-tight">
            Compare GoHost Against Traditional Restaurant Stacks
          </h2>
          <p className="text-lg font-bold text-yellow-700">
            Standards we build into one unified system, others bolt on and bill for.
          </p>
        </div>

        {/* POS Services Table */}
        <div>
          <p className="mb-1 text-center text-sm font-bold uppercase tracking-widest text-neutral-500 ">POS Services</p>
          <div className="overflow-x-auto rounded-2xl border-2 border-neutral-300 shadow-xl">
            <table className="min-w-full border-collapse text-base">
              <thead>
                <tr>
                  <th className={`${cell} text-left text-base font-bold bg-neutral-100`}>Service</th>
                  <th className={`${cell} text-left text-base font-bold ${BRAND.gohost.th}`}>GoHost</th>
                  <th className={`${cell} text-left text-base font-bold ${BRAND.toast.th}`}>Toast</th>
                  <th className={`${cell} text-left text-base font-bold ${BRAND.spoton.th}`}>SpotOn</th>
                  <th className={`${cell} text-left text-base font-bold ${BRAND.clover.th}`}>Clover</th>
                  <th className={`${cell} text-left text-base font-bold ${BRAND.square.th}`}>Square</th>
                  <th className={`${cell} text-left text-base font-bold ${BRAND.lightspeed.th}`}>Lightspeed</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: "Table Management & Guest Notes",                key: "pos" },
                  { label: "Website",                     key: "website" },
                  { label: "Online Ordering / Preorders", key: "ordering" },
                  { label: "Loyalty",                     key: "loyalty" },
                  { label: "SMS Messaging",               key: "sms" },
                  { label: "Gift Cards",                         key: "giftcards" },
                  { label: "Advanced Menu Management",          key: "advancedMenu" },
                  { label: "Basic Reporting & Inventory",       key: "basicReporting" },
                  { label: "Unlimited Waitlist & Reservations", key: "waitlistReservations" },
                ].map(({ label, key }) => (
                  <tr key={key}>
                    <td className={`${cell} font-medium bg-white`}>{label}</td>
                    <td className={`${cell} ${BRAND.gohost.col}`}>
                      <span className={labelClass(PRICING.gohost[key].label)}>{PRICING.gohost[key].label}</span>
                      {PRICING.gohost[key].labelSuffix && (
                        <span className="text-red-700"> {PRICING.gohost[key].labelSuffix}</span>
                      )}
                    </td>
                    <td className={`${cell} ${labelClass(PRICING.toast[key].label)} ${BRAND.toast.col}`}>{PRICING.toast[key].label}</td>
                    <td className={`${cell} ${labelClass(PRICING.spoton[key].label)} ${BRAND.spoton.col}`}>{PRICING.spoton[key].label}</td>
                    <td className={`${cell} ${labelClass(PRICING.clover[key].label)} ${BRAND.clover.col}`}>{PRICING.clover[key].label}</td>
                    <td className={`${cell} ${labelClass(PRICING.square[key].label)} ${BRAND.square.col}`}>{PRICING.square[key].label}</td>
                    <td className={`${cell} ${labelClass(PRICING.lightspeed[key].label)} ${BRAND.lightspeed.col}`}>{PRICING.lightspeed[key].label}</td>
                  </tr>
                ))}
                <tr>
                  <td className={`${cell} font-medium bg-white`}>Payroll</td>
                  <td className={`${cell} text-green-700 ${BRAND.gohost.col}`}>Included</td>
                  <td className={`${cell} text-red-700 ${BRAND.toast.col}`}>{PRICING.toast.payroll.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.spoton.col}`}>{PRICING.spoton.payroll.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.clover.col}`}>{PRICING.clover.payroll.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.square.col}`}>{PRICING.square.payroll.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.lightspeed.col}`}>{PRICING.lightspeed.payroll.label}</td>
                </tr>
                <tr>
                  <td className={`${cell} font-medium bg-white`}>Employee Scheduling</td>
                  <td className={`${cell} text-green-700 ${BRAND.gohost.col}`}>Included</td>
                  <td className={`${cell} text-red-700 ${BRAND.toast.col}`}>{PRICING.toast.employeeScheduling.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.spoton.col}`}>{PRICING.spoton.employeeScheduling.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.clover.col}`}>{PRICING.clover.employeeScheduling.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.square.col}`}>{PRICING.square.employeeScheduling.label}</td>
                  <td className={`${cell} text-green-700 ${BRAND.lightspeed.col}`}>{PRICING.lightspeed.employeeScheduling.label}</td>
                </tr>
                <tr>
                  <td className={`${cell} font-medium bg-white`}>Card Processing</td>
                  <td className={`${cell} text-red-700 ${BRAND.gohost.col}`}>
                    <div>{PRICING.gohost.processing.label}</div>
                    {/* <div className="text-xs text-neutral-500">At current inputs: {formatMoney(processingBreakdown.gohostTotal)}/yr</div> */}
                  </td>
                  <td className={`${cell} text-red-700 ${BRAND.toast.col}`}>{PRICING.toast.processing.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.spoton.col}`}>{PRICING.spoton.processing.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.clover.col}`}>{PRICING.clover.processing.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.square.col}`}>{PRICING.square.processing.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.lightspeed.col}`}>{PRICING.lightspeed.processing.label}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Reservation Platforms Table */}
        <div>
          <p className="mb-1 text-center text-sm font-bold uppercase tracking-widest text-neutral-500">Reservation Platforms</p>
          <div className="overflow-x-auto rounded-2xl border-2 border-neutral-300 shadow-xl">
            <table className="min-w-full border-collapse text-base">
              <thead>
                <tr>
                  <th className={`${cell} text-left text-base font-bold bg-neutral-100`}>Service</th>
                  <th className={`${cell} text-left text-base font-bold ${BRAND.gohost.th}`}>GoHost</th>
                  <th className={`${cell} text-left text-base font-bold ${BRAND.opentable.th}`}>OpenTable (Core)</th>
                  <th className={`${cell} text-left text-base font-bold ${BRAND.resy.th}`}>Resy (Essenital)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={`${cell} font-medium bg-white`}>Online Reservations</td>
                  <td className={`${cell} text-green-700 ${BRAND.gohost.col}`}>{PRICING.gohost.reservations.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.opentable.col}`}>{PRICING.opentable.reservations.label}</td>
                  <td className={`${cell} text-red-700 ${BRAND.resy.col}`}>{PRICING.resy.reservations.label}</td>
                </tr>
                {/* <tr>
                  <td className={`${cell} font-medium bg-white`}>Yearly Base</td>
                  <td className={`${cell} text-green-700 ${BRAND.gohost.col}`}>Included</td>
                  <td className={`${cell} text-red-700 ${BRAND.opentable.col}`}>{formatMoney(PRICING.opentable.reservations.yearly)}</td>
                  <td className={`${cell} text-red-700 ${BRAND.resy.col}`}>{formatMoney(PRICING.resy.reservations.yearly)}</td>
                </tr> */}
                <tr>
                  <td className={`${cell} font-medium bg-white`}>Per Cover</td>
                  <td className={`${cell} text-green-700 ${BRAND.gohost.col}`}>Included</td>
                  <td className={`${cell} text-red-700 ${BRAND.opentable.col}`}>{formatMoneyPrecise(PRICING.opentable.reservations.perCover)}</td>
                  <td className={`${cell} text-red-700 ${BRAND.resy.col}`}>{formatMoneyPrecise(PRICING.resy.reservations.perCover)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Combined: Competitor Selectors + Services + Inputs */}
        <div className="rounded-2xl border-2 border-neutral-300 bg-white p-4 space-y-3 shadow-xl">

          {/* Volume Inputs */}
          <div className="text-center">
            <p className="h2 text-2xl capitalize mb-2">calculate costs with your numbers</p>
            <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-neutral-600">Volume Inputs</p>
            <div className="mx-auto grid max-w-4xl gap-3 md:grid-cols-4">
              <div className="text-left">
                <label className="mb-1 block text-sm font-medium text-neutral-700">Average Ticket Price</label>
                <input
                  type="number"
                  value={avgTicket}
                  onChange={(e) => setAvgTicket(Number(e.target.value) || 0)}
                  className="h-9 w-full rounded-xl border border-neutral-300 px-3 outline-none focus:border-neutral-500"
                  placeholder="100"
                />
                <p className="mt-1 text-xs text-neutral-500">Used for percentage-based card processing totals.</p>
              </div>
              <div className="text-left">
                <label className="mb-1 block text-sm font-medium text-neutral-700">Total Tickets / Covers</label>
                <input
                  type="number"
                  value={totalTickets}
                  onChange={(e) => setTotalTickets(Number(e.target.value) || 0)}
                  className="h-9 w-full rounded-xl border border-neutral-300 px-3 outline-none focus:border-neutral-500"
                  placeholder="12000"
                />
                <p className="mt-1 text-xs text-neutral-500">Used for per-ticket processing fees.</p>
              </div>
              <div className="text-left">
                <label className="mb-1 block text-sm font-medium text-neutral-700">Online Reservations</label>
                <input
                  type="number"
                  value={onlineReservations}
                  onChange={(e) => setOnlineReservations(Number(e.target.value) || 0)}
                  className="h-9 w-full rounded-xl border border-neutral-300 px-3 outline-none focus:border-neutral-500"
                  placeholder="8000"
                />
                <p className="mt-1 text-xs text-neutral-500">Used for per-cover reservation fees and SMS usage.</p>
              </div>
              <div className="text-left">
                <label className="mb-1 block text-sm font-medium text-neutral-700">Employees</label>
                <input
                  type="number"
                  value={numEmployees}
                  onChange={(e) => setNumEmployees(Number(e.target.value) || 0)}
                  className="h-9 w-full rounded-xl border border-neutral-300 px-3 outline-none focus:border-neutral-500"
                  placeholder="10"
                />
                <p className="mt-1 text-xs text-neutral-500">Used to calculate per-employee payroll and scheduling costs.</p>
              </div>
            </div>
          </div>

          <hr className="border-neutral-200" />

          {/* Services */}
          <div className="text-center">
            <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-neutral-600">Select Services</p>
            <div className="flex flex-wrap justify-center gap-2">
              {SERVICES.map((service) => {
                const isSelected = selectedServices.includes(service.key);
                return (
                  <button
                    key={service.key}
                    type="button"
                    onClick={() => toggleService(service.key)}
                    className={`rounded-full border px-4 py-1 text-sm font-medium transition ${
                      isSelected ? "border-black bg-black text-white" : "border-neutral-300 bg-white text-neutral-800"
                    }`}
                  >
                    {service.label}
                  </button>
                );
              })}
            </div>
          </div>

          <hr className="border-neutral-200" />

          {/* Competitor row — always side-by-side */}
          <div>
            <p className="mb-2 text-center text-sm font-semibold uppercase tracking-wide text-neutral-600">Compare Against</p>
            <div className="flex items-start">

              <div className="flex flex-1 flex-col items-center gap-1">
                <span className="text-sm font-bold uppercase tracking-wider text-neutral-700">POS Services</span>
                <div className="flex flex-wrap justify-center gap-2">
                  {["toast", "spoton", "clover", "square", "lightspeed"].map((provider) => (
                    <button
                      key={provider}
                      type="button"
                      onClick={() => setSelectedPosProvider(provider)}
                      className={`rounded-full border px-4 py-1 text-sm font-medium transition ${
                        selectedPosProvider === provider ? BRAND[provider].sel : BRAND[provider].unsel
                      }`}
                    >
                      {PROVIDER_DISPLAY_NAME[provider]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mx-3 self-stretch w-0.5 rounded-full bg-neutral-400" />

              <div className="flex flex-1 flex-col items-center gap-1">
                <span className="text-sm font-bold uppercase tracking-wider text-neutral-700">Online Reservation Platforms</span>
                <p className="text-xs text-neutral-400 -mt-0.5">Select one to include platform fees, or deselect if you don't use a third-party reservation system.</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {["opentable", "resy"].map((provider) => (
                    <button
                      key={provider}
                      type="button"
                      onClick={() => setSelectedReservationProvider((prev) => prev === provider ? null : provider)}
                      className={`rounded-full border px-4 py-1 text-sm font-medium transition ${
                        selectedReservationProvider === provider ? BRAND[provider].sel : BRAND[provider].unsel
                      }`}
                    >
                      {provider === "opentable" ? "OpenTable" : "Resy"}
                    </button>
                  ))}
                </div>
              </div>

            </div>
          </div>

          <hr className="border-neutral-200" />

          {/* Totals — live inside the card so selections visibly drive the numbers */}
          <div>
            <p className="mb-2 text-center text-sm font-semibold uppercase tracking-wide text-neutral-600">Annual Cost Breakdown</p>

            <div className="grid gap-2 md:grid-cols-2">

              {/* GoHost — centered on mobile, right-aligned on desktop */}
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 shadow-sm text-center md:text-right">
                <div className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-amber-700">GoHost</div>
                <div className="text-2xl font-bold text-amber-900">{formatMoney(totals.gohost)}/yr</div>
                {(totals.showProcessing || totals.showSoftware || totals.showReservations) && (
                  <div className="mt-1 space-y-0.5 border-t border-amber-200 pt-1">
                    {totals.showProcessing && (
                      <div>
                        <div className="flex items-baseline justify-center gap-2 text-sm md:justify-end">
                          <span className="text-amber-700">Card Processing</span>
                          <span className="w-20 shrink-0 text-right font-medium text-amber-900">{formatMoney(totals.subtotals.gohost.processing)}</span>
                        </div>
                        <div className="flex items-baseline justify-center gap-2 text-xs md:justify-end mt-0.5 text-amber-600 md:pr-2 pl-4 md:pl-0">
                          <span>(Stripe — 2.7% + $0.05/txn)</span>
                          <span className="w-20 shrink-0 text-right">{formatMoney(totals.subtotals.gohost.processingStripe)}</span>
                        </div>
                        <div className="flex items-baseline justify-center gap-2 text-xs md:justify-end mt-0.5 text-amber-600 md:pr-2 pl-4 md:pl-0">
                          <span>{`(GoHost — $${gohostTxnFee.toFixed(2)}/txn)`}</span>
                          <span className="w-20 shrink-0 text-right">{formatMoney(totals.subtotals.gohost.processingGoHost)}</span>
                        </div>
                      </div>
                    )}
                    {totals.showSoftware && (
                      <div>
                        <div className="flex items-baseline justify-center gap-2 text-sm md:justify-end">
                          <span className="text-amber-700">Software &amp; Services</span>
                          <span className="w-20 shrink-0 text-right font-medium text-amber-900">{formatMoney(totals.subtotals.gohost.software)}</span>
                        </div>
                        {totals.subtotals.gohost.softwareItems
                          .filter((item) => item.amount > 0)
                          .map((item) => (
                            <div key={item.label} className="flex items-baseline justify-center gap-2 text-xs md:justify-end mt-0.5 text-amber-600 md:pr-2 pl-4 md:pl-0">
                              <span>({item.label})</span>
                              <span className="w-20 shrink-0 text-right">{formatMoney(item.amount)}</span>
                            </div>
                          ))}
                      </div>
                    )}
                    {totals.showReservations && (
                      <div className="flex items-baseline justify-center gap-2 text-sm md:justify-end">
                        <span className="text-amber-700">Reservations</span>
                        <span className="w-20 shrink-0 text-right font-medium text-green-700">Included</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Competitor — centered on mobile, left-aligned on desktop */}
              <div className="rounded-xl border-2 border-red-300 bg-red-50 p-3 shadow-sm text-center md:text-left">
                <div className="mb-0.5 text-xs font-semibold uppercase tracking-wide">
                  <span className={BRAND[selectedPosProvider].text}>
                    {PROVIDER_DISPLAY_NAME[selectedPosProvider]}
                  </span>
                  {reservationProviderLabel && (
                    <>
                      <span className="text-neutral-400"> + </span>
                      <span className={BRAND[selectedReservationProvider].text}>
                        {reservationProviderLabel}
                      </span>
                    </>
                  )}
                </div>
                <div className="text-2xl font-bold text-red-900">{formatMoney(totals.competitor)}/yr</div>
                {(totals.showProcessing || totals.showSoftware || totals.showReservations) && (
                  <div className="mt-1 space-y-0.5 border-t border-red-200 pt-1">
                    {totals.showProcessing && (
                      <div className="flex items-baseline justify-center gap-2 text-sm md:justify-start flex-wrap">
                        <span className="w-20 shrink-0 font-medium text-red-900">{formatMoney(totals.subtotals.competitor.processing)}</span>
                        <span className="text-red-700">
                          Card Processing
                          <span className="ml-1 text-xs text-red-400">({PRICING[selectedPosProvider].processing.label})</span>
                        </span>
                      </div>
                    )}
                    {totals.showSoftware && (
                      <div>
                        <div className="flex items-baseline justify-center gap-2 text-sm md:justify-start">
                          <span className="w-20 shrink-0 font-medium text-red-900">{formatMoney(totals.subtotals.competitor.software)}</span>
                          <span className="text-red-700">Software &amp; Services</span>
                        </div>
                        {totals.subtotals.competitor.softwareItems.map((item) => (
                          <div key={item.label} className="flex items-baseline justify-center gap-2 text-xs md:justify-start mt-0.5 text-red-400 md:pl-2 pr-4 md:pr-0">
                            <span className="w-20 shrink-0 text-right">{formatMoney(item.amount)}</span>
                            <span>({item.label})</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {totals.showReservations && (
                      <div className="flex items-baseline justify-center gap-2 text-sm md:justify-start">
                        <span className="w-20 shrink-0 font-medium text-red-900">{formatMoney(totals.subtotals.competitor.reservations)}</span>
                        <span className="text-red-700">Reservations</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>

            {/* Savings */}
            <div className="mt-2 rounded-xl border border-neutral-200 bg-white px-4 py-2 text-center">
              <div className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">Savings Comparison</div>
              <div className={`text-xl font-bold ${totals.savings >= 0 ? "text-green-700" : "text-red-700"}`}>
                {totals.savings >= 0
                  ? `${formatMoney(totals.savings)} saved with GoHost`
                  : `${formatMoney(Math.abs(totals.savings))} more with GoHost`}
              </div>
            </div>

          </div>

        </div>

        {/* Disclaimer */}
        <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-500 text-center">
Pricing estimates are based on publicly available pricing and industry reports. Actual costs may vary based on negotiated rates, contract terms, usage volume, and add-on selections. This tool is intended for directional comparison purposes only and does not represent a binding quote from any provider or GoHost. </div>

      </div>
    </div>
  );
}
